import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import GHGEmissionMasterDataService from "../../core/service/GHGEmissionMasterDataService";
import GHGEmissionService from "../../core/service/GHGEmissionService";

const EmissionVersionFields = ({
  t,
  companyId,
  glbRegulationId,
  editDatabase = "",
  editVersionRef = "",
  onScopeDataChange,
  onScopeCategoryReset,
  onConfigChange,
  validated = false,
}) => {
  const [emissionTableList, setEmissionTableList] = useState([]);
  const [linkFrom, setLinkFrom] = useState("company");
  const [selectedTable, setSelectedTable] = useState("");
  const [selectedVersion, setSelectedVersion] = useState("");
  const [versions, setVersions] = useState([]);
  const [editInitialized, setEditInitialized] = useState(false);

  useEffect(() => {
    if (glbRegulationId && emissionTableList.length === 0) {
      GHGEmissionService.getDataSet(glbRegulationId)
        .then((response) => {
          setEmissionTableList(response.data?.data || []);
        })
        .catch(() => { });
    }
  }, [glbRegulationId, emissionTableList.length]);

  useEffect(() => {
    onConfigChange?.({
      database: selectedTable,
      versionRef: selectedVersion,
      linkFrom,
      isValid: Boolean(selectedTable && selectedVersion),
    });
  }, [selectedTable, selectedVersion, linkFrom, onConfigChange]);

  const loadVersions = (table, linkFromValue, callback) => {
    if (!table) return;
    const fetchCompanyId = linkFromValue === "company" ? companyId : null;
    GHGEmissionMasterDataService.listVersions(table, fetchCompanyId)
      .then((res) => {
        if (res.status === 200) {
          const versionsList = Array.isArray(res.data) ? res.data : [res.data].filter(Boolean);
          setVersions(versionsList);
          if (callback) callback(versionsList);
        }
      })
      .catch(() => {
        setVersions([]);
      });
  };

  const fetchScopeDetails = (versionId, db) => {
    if (!versionId || !db) {
      onScopeDataChange?.([]);
      return;
    }
    GHGEmissionService.getVersionScopeDetails(versionId, db)
      .then((response) => {
        if (response.status === 200) {
          const data = response.data?.data ?? response.data ?? [];
          onScopeDataChange?.(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        onScopeDataChange?.([]);
      });
  };

  const resetScopeSelection = () => {
    onScopeCategoryReset?.();
    onScopeDataChange?.([]);
  };

  const handleLinkFromChange = (newLinkFrom) => {
    setLinkFrom(newLinkFrom);
    setSelectedVersion("");
    setVersions([]);
    resetScopeSelection();
    if (selectedTable) {
      loadVersions(selectedTable, newLinkFrom);
    }
  };

  const handleTableChange = (newTable) => {
    setSelectedTable(newTable);
    setSelectedVersion("");
    setVersions([]);
    resetScopeSelection();
    if (newTable) {
      loadVersions(newTable, linkFrom);
    }
  };

  const handleVersionChange = (versionId) => {
    setSelectedVersion(versionId);
    resetScopeSelection();
    if (versionId && selectedTable) {
      fetchScopeDetails(versionId, selectedTable);
    }
  };

  const isInvalid = (val) => validated && !val;
  const isConfigIncomplete = !(selectedTable && selectedVersion);

  useEffect(() => {
    if (!editDatabase || editInitialized || emissionTableList.length === 0) {
      return;
    }

    const initEdit = async () => {
      setSelectedTable(editDatabase);
      if (editVersionRef) {
        try {
          const versionsRes = await GHGEmissionMasterDataService.listVersions(editDatabase, null, true);
          if (versionsRes.status === 200) {
            const allVersions = Array.isArray(versionsRes.data) ? versionsRes.data : [versionsRes.data].filter(Boolean);
            const savedVersion = allVersions.find((v) => v.id === editVersionRef);
            if (savedVersion) {
              const hasCompanyId = savedVersion.companyId && companyId && savedVersion.companyId === companyId;
              const determinedLinkFrom = hasCompanyId ? "company" : "global";
              setLinkFrom(determinedLinkFrom);
              const fetchCompanyId = determinedLinkFrom === "company" ? companyId : null;
              const res = await GHGEmissionMasterDataService.listVersions(editDatabase, fetchCompanyId);
              if (res.status === 200) {
                const versionsList = Array.isArray(res.data) ? res.data : [res.data].filter(Boolean);
                setVersions(versionsList);
                setSelectedVersion(editVersionRef);
                fetchScopeDetails(editVersionRef, editDatabase);
              }
            }
          }
        } catch {
          loadVersions(editDatabase, linkFrom, () => {
            if (editVersionRef) {
              setSelectedVersion(editVersionRef);
              fetchScopeDetails(editVersionRef, editDatabase);
            }
          });
        }
      } else {
        loadVersions(editDatabase, linkFrom);
      }
      setEditInitialized(true);
    };

    initEdit();
  }, [editDatabase, editVersionRef, editInitialized, emissionTableList.length, companyId]);

  return (
    <div className="emission-version-fields" data-testid="emission-version-fields">
      <div className="row mb-2">
        <div className="col-12">
          <Form.Label>
            {t("LinkEmissionFactor.linkFrom.label")}
            <span className="text-danger ms-1" aria-hidden="true">*</span>
          </Form.Label>
          <div>
            <Form.Check
              inline
              type="radio"
              label={t("LinkEmissionFactor.companyDatasets.label")}
              name="emissionLinkFrom"
              id="emissionLinkFromCompany"
              checked={linkFrom === "company"}
              onChange={() => handleLinkFromChange("company")}
            />
            <Form.Check
              inline
              type="radio"
              label={t("LinkEmissionFactor.globalDatasets.label")}
              name="emissionLinkFrom"
              id="emissionLinkFromGlobal"
              checked={linkFrom === "global"}
              onChange={() => handleLinkFromChange("global")}
            />
          </div>
        </div>
      </div>
      <div className="row mb-2">
        <div className="col-md-6">
          <label className="mb-1">
            {t("EmissionTable.label")}
            <span className="text-danger ms-1" aria-hidden="true">*</span>
          </label>
          <select
            id="emissionTableSelect"
            className={`form-select${isInvalid(selectedTable) ? " border-danger" : ""}`}
            required
            value={selectedTable}
            onChange={(e) => handleTableChange(e.target.value)}
          >
            <option value="">{t("Select.label")}</option>
            {(emissionTableList || []).map((table) => (
              <option key={table.id} value={table.name}>
                {table.name}
              </option>
            ))}
          </select>
          {validated && isConfigIncomplete && (
            <div className="text-danger small mt-2">{t("EmissionVersionFields.table.label")}</div>
          )}
        </div>
        <div className="col-md-6">
          <label className="mb-1">
            {t("Version.label")}
            <span className="text-danger ms-1" aria-hidden="true">*</span>
          </label>
          <select
            id="emissionVersionSelect"
            className={`form-select${isInvalid(selectedVersion) ? " border-danger" : ""}`}
            required
            value={selectedVersion}
            onChange={(e) => handleVersionChange(e.target.value)}
            disabled={!selectedTable}
          >
            <option value="">{t("Select.label")}</option>
            {(versions || []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.versionName || `Version ${v.versionNumber}`}
              </option>
            ))}
          </select>
          {validated && isConfigIncomplete && (
            <div className="text-danger small mt-2">{t("EmissionVersionFields.version.label")}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(EmissionVersionFields);
